import React, { useState, useEffect } from 'react';
import { getCachedImage } from '../services/imageCache';

interface CachedImageProps {
  src: string;
  alt: string;
  style?: React.CSSProperties;
  className?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  fallback?: React.ReactNode;
}

export const CachedImage: React.FC<CachedImageProps> = ({
  src,
  alt,
  style,
  className,
  onError,
  fallback
}) => {
  const [cachedSrc, setCachedSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setHasError(false);

    const loadImage = async () => {
      if (!src) {
        setIsLoading(false);
        return;
      }

      const cached = await getCachedImage(src);
      if (isMounted) {
        setCachedSrc(cached);
        setIsLoading(false);
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [src]);

  if (hasError && fallback) {
    return <>{fallback}</>;
  }

  if (isLoading || !cachedSrc) {
    return (
      <div
        style={{
          ...style,
          backgroundColor: '#e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        className={className}
      />
    );
  }

  return (
    <img
      src={cachedSrc}
      alt={alt}
      style={style}
      className={className}
      onError={(e) => {
        setHasError(true);
        onError?.(e);
      }}
    />
  );
};
